import React, { useState } from 'react';
import Navbar from "../components/Navbar";
import { motion } from "framer-motion";
import ImageModal from "../components/ImageModal";
import {
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const Services = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const servicesPerPage = 6;

  const openImageModal = (imageSrc, imageAlt) => {
    setSelectedImage({ src: imageSrc, alt: imageAlt });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const categories = [
    { id: 'all', name: 'All Services' },
    { id: 'indoor', name: 'Indoor Sports' },
    { id: 'outdoor', name: 'Outdoor Sports' },
    { id: 'track', name: 'Running Tracks' },
    { id: 'multi', name: 'Multi-Sport' }
  ];

  const services = [
    {
      title: "Squash Court Flooring",
      description: "Professional-grade squash court flooring with optimal shock absorption and ball bounce characteristics. Our flooring solutions meet international standards and provide excellent player comfort.",
      image: "/service/service1.jpg",
      category: "indoor",
      features: [
        "Anti-slip surface",
        "Shock absorption",
        "Easy maintenance",
        "Long-lasting durability"
      ],
      benefits: "Enhanced player performance and safety with professional-grade materials",
      rating: 5
    },
    {
      title: "Tennis Court Flooring",
      description: "High-performance tennis court surfaces designed for optimal ball bounce and player movement. Available in various materials including acrylic, artificial grass, and clay alternatives.",
      image: "/service/Service2.jpg",
      category: "outdoor",
      features: [
        "Professional-grade materials",
        "Weather resistance",
        "Custom line marking",
        "Drainage system"
      ],
      benefits: "Perfect for both recreational and professional tennis facilities",
      rating: 5
    },
    {
      title: "Badminton Court Flooring",
      description: "Specialized flooring solutions for badminton courts with proper cushioning and grip. Our flooring ensures consistent ball bounce and reduces player fatigue.",
      image: "/service/Service3.jpg",
      category: "indoor",
      features: [
        "Shock absorption",
        "Anti-slip surface",
        "Easy cleaning",
        "Professional line marking"
      ],
      benefits: "Ideal for both training and competitive play",
      rating: 5
    },
    {
      title: "Basketball Court Flooring",
      description: "Professional basketball court flooring with excellent ball bounce and player comfort. Available in various materials including wooden, synthetic, and modular options.",
      image: "/service/Service4.jpg",
      category: "indoor",
      features: [
        "High-performance surface",
        "Shock absorption",
        "Durable construction",
        "Custom line marking"
      ],
      benefits: "Suitable for both indoor and outdoor basketball facilities",
      rating: 5
    },
    {
      title: "Volleyball Court Flooring",
      description: "Specialized volleyball court surfaces designed for optimal player movement and ball control. Our flooring solutions ensure consistent performance and safety.",
      image: "/service/Service5.jpg",
      category: "outdoor",
      features: [
        "Professional-grade materials",
        "Weather resistance",
        "Anti-slip surface",
        "Easy maintenance"
      ],
      benefits: "Perfect for both beach and indoor volleyball facilities",
      rating: 5
    },
    {
      title: "Jogging & Running Track",
      description: "Professional-grade running tracks with proper drainage and shock absorption. Our tracks are designed to reduce impact on joints and provide optimal running conditions.",
      image: "/service/Service6.jpg",
      category: "track",
      features: [
        "Shock absorption",
        "Drainage system",
        "Weather resistance",
        "Professional line marking"
      ],
      benefits: "Ideal for both training and competitive athletics",
      rating: 5
    },
    {
      title: "Football & Futsal Grounds",
      description: "High-quality artificial grass and natural turf solutions for football and futsal facilities. Our surfaces provide excellent ball control and player safety.",
      image: "/service/Service7.jpg",
      category: "outdoor",
      features: [
        "Professional-grade turf",
        "Drainage system",
        "Weather resistance",
        "Custom line marking"
      ],
      benefits: "Suitable for both training and competitive matches",
      rating: 5
    },
    {
      title: "Cricket Pitch & Ground",
      description: "Complete cricket infrastructure solutions including pitch preparation, outfield maintenance, and practice facilities. Our solutions meet international cricket standards.",
      image: "/service/Service8.jpg",
      category: "outdoor",
      features: [
        "Professional pitch preparation",
        "Drainage system",
        "Practice facilities",
        "Maintenance services"
      ],
      benefits: "Perfect for both training and competitive cricket facilities",
      rating: 5
    },
    {
      title: "Pickleball Courts",
      description: "Professional pickleball court construction with specialized surfaces designed for optimal ball bounce and player movement. Our courts meet international pickleball standards and provide excellent playing conditions.",
      image: "/service/Service9.jpg",
      category: "outdoor",
      features: [
        "Professional-grade surface",
        "Weather-resistant materials",
        "Custom line marking",
        "Proper court dimensions"
      ],
      benefits: "Perfect for both recreational and competitive pickleball facilities",
      rating: 5
    },
    {
      title: "Padel Courts",
      description: "High-quality padel court construction with specialized surfaces designed for optimal ball bounce and player movement. Our courts meet international padel standards and provide excellent playing conditions.",
      image: "/service/Service10.jpg",
      category: "outdoor",
      features: [
        "Professional-grade surface",
        "Weather-resistant materials",
        "Custom line marking",
        "Proper court dimensions"
      ],
      benefits: "Perfect for both recreational and competitive padel facilities",
      rating: 5
    }
  ];

  const filteredServices = activeCategory === 'all' 
    ? services 
    : services.filter(service => service.category === activeCategory);

  const indexOfLastService = currentPage * servicesPerPage;
  const indexOfFirstService = indexOfLastService - servicesPerPage;
  const currentServices = filteredServices.slice(indexOfFirstService, indexOfLastService);
  const totalPages = Math.ceil(filteredServices.length / servicesPerPage);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-gray-800 to-orange-700 text-white py-16 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 text-center">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-200 to-orange-200 mb-4 md:mb-6"
            >
              Our Services
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-base md:text-lg lg:text-xl text-gray-100 max-w-2xl mx-auto px-4"
            >
              Discover our comprehensive range of professional sports infrastructure solutions designed to elevate your facility to international standards.
            </motion.p>
          </div>
        </section>

        {/* Category Filters */}
        <section className="py-8 bg-gradient-to-br from-gray-50 to-orange-50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setCurrentPage(1);
                  }}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                    activeCategory === category.id
                      ? 'bg-green-800 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100 hover:shadow-md'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {currentServices.map((service, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="group bg-white rounded-3xl shadow-xl overflow-hidden transform transition duration-300 hover:scale-[1.02] hover:shadow-2xl border border-gray-100"
                >
                  <div className="relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-48 sm:h-56 md:h-64 object-cover transition-transform duration-500 group-hover:scale-110 cursor-pointer"
                      onClick={() => openImageModal(service.image, service.title)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute top-4 right-4 flex items-center bg-white/90 px-3 py-1 rounded-full">
                      {[...Array(service.rating)].map((_, i) => (
                        <StarIcon key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    {/* Click to view overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-black/70 rounded-full p-3 text-white">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-orange-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Features:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-600">
                            <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold">Benefits: </span>
                      {service.benefits}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-12">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-full ${
                    currentPage === 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-green-800 hover:bg-green-50 border border-gray-200'
                  }`}
                >
                  <ChevronLeftIcon className="w-6 h-6" />
                </button>
                <span className="text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-full ${
                    currentPage === totalPages
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-green-800 hover:bg-green-50 border border-gray-200'
                  }`}
                >
                  <ChevronRightIcon className="w-6 h-6" />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Image Modal */}
        <ImageModal
          isOpen={selectedImage !== null}
          imageSrc={selectedImage?.src}
          imageAlt={selectedImage?.alt}
          onClose={closeImageModal}
        />
      </div>
    </>
  );
};

export default Services;
